import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleBundleRemoveClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleBundleRemoveClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles click on "Remove Bundle" button.
   * @param {Event} event
   */
  #handleBundleRemoveClick = (event) => {
    const removeBtn = event.target.closest('.cart-bundle-remove-all');
    if (!removeBtn) return;

    event.preventDefault();

    const bundleKeys = removeBtn.dataset.bundleKeys;
    if (!bundleKeys) return;

    const keys = bundleKeys.split(',');
    this.#removeBundleItems(keys, removeBtn);
  };

  /**
   * Removes all bundle items by their keys in a single API call.
   * @param {string[]} keys - Array of cart item keys to remove.
   * @param {HTMLElement} triggerBtn - The button that triggered the removal.
   */
  async #removeBundleItems(keys, triggerBtn) {
    this.#disableCartItems();

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    // Animate out the bundle header and all bundle item rows
    const bundleId = triggerBtn.closest('.cart-bundle-header')?.dataset.bundleId;
    if (bundleId) {
      const bundleRows = this.querySelectorAll(
        `.cart-bundle-header[data-bundle-id="${bundleId}"], .cart-bundle-item[data-bundle-id="${bundleId}"]`
      );
      bundleRows.forEach((row) => {
        const remove = () => row.remove();
        if (prefersReducedMotion()) return remove();
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      });
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }),
      });

      let parsedResponse = await response.json();

      // Clean up non-applicable discount codes (e.g., bundle discount no longer qualifying)
      const nonApplicableCodes = (parsedResponse.discount_codes || [])
        .filter((d) => !d.applicable)
        .map((d) => d.code);

      if (nonApplicableCodes.length > 0) {
        const applicableCodes = (parsedResponse.discount_codes || [])
          .filter((d) => d.applicable)
          .map((d) => d.code);

        const cleanupResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount: applicableCodes.join(','),
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        });
        parsedResponse = await cleanupResponse.json();
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponse.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponse.sections,
        })
      );

      morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);
    } catch (error) {
      console.error('Error removing bundle:', error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    // The line's key, to find it again after a re-render (line numbers can shift).
    const lineKey = this.querySelector(`[data-cart-line="${line}"]`)?.closest('[ref="cartItemRows[]"]')?.getAttribute('data-key') || null;

    try {
      // Single API call to update quantity and get sections
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponseText = JSON.parse(await response.text());

      resetShimmer(this);

      if (parsedResponseText.errors) {
        await this.#recoverFromCartError(line, lineKey, parsedResponseText, sectionsToUpdate);
        return;
      }

      // Handle engraving fee updates if needed (uses cart data from response)
      await this.#updateEngravingFeeIfNeeded(parsedResponseText, line, quantity, sectionsToUpdate);

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates engraving fee quantities for both one-line and two-line fees.
   * Only makes an API call if fee adjustment is needed.
   * @param {Object} cartResponse - The cart response from the main update.
   * @param {number} originalLine - The original line that was updated.
   * @param {number} newQuantity - The new quantity of the main item.
   * @param {Set<string>} sectionsToUpdate - Sections to include in the update.
   */
  async #updateEngravingFeeIfNeeded(cartResponse, originalLine, newQuantity, sectionsToUpdate) {
    const items = cartResponse.items;
    if (!items) return;

    const FEE_ONE_LINE = 43781283217459;
    const FEE_TWO_LINES = 43781283250227;

    // Calculate required quantities for BOTH fee types
    let requiredOneLine = 0;
    let requiredTwoLine = 0;

    items.forEach(item => {
      if (item.properties && item.properties["Engraving Text"]) {
        const knifeQty = parseInt(item.properties["Knife Quantity"]) || 1;
        const qty = item.quantity * knifeQty;
        
        if (item.properties["Engraving Text2"]) {
          requiredTwoLine += qty;
        } else {
          requiredOneLine += qty;
        }
      }
    });

    // Find current fee items
    const feeOneItem = items.find(i => i.variant_id === FEE_ONE_LINE);
    const feeTwoItem = items.find(i => i.variant_id === FEE_TWO_LINES);

    const currentOneLine = feeOneItem?.quantity || 0;
    const currentTwoLine = feeTwoItem?.quantity || 0;

    // Build updates object for any fees that need adjustment
    const updates = {};
    if (currentOneLine !== requiredOneLine) {
      updates[FEE_ONE_LINE] = requiredOneLine;
    }
    if (currentTwoLine !== requiredTwoLine) {
      updates[FEE_TWO_LINES] = requiredTwoLine;
    }

    // Only make API call if updates are needed
    if (Object.keys(updates).length === 0) return;

    // Use /cart/update.js to update both fee types in a single request
    const feeUpdateResponse = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      })
    });

    // Update the sections in the original response with the new sections
    const feeUpdateData = await feeUpdateResponse.json();
    if (feeUpdateData.sections) {
      Object.assign(cartResponse.sections, feeUpdateData.sections);
      cartResponse.items = feeUpdateData.items;
      cartResponse.item_count = feeUpdateData.item_count;
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * A /cart/change error can still have changed the cart: over stock, Shopify answers 422
   * {errors: 'Only 4 items were added to your cart due to availability.'} with no sections, yet it
   * applies the capped quantity. Resetting the input to its old value then left the page, the count
   * and the engraving fee out of step with the real cart (and a later + worked from the stale value).
   * Read the real cart and its sections, sync the engraving fee to it, announce and morph exactly as a
   * successful change does, then show the message on the re-rendered line.
   * @param {number} line - The 1-based line that was changed.
   * @param {string | null} lineKey - That line's key before the change.
   * @param {{errors: string}} parsedResponseText - The error response.
   * @param {Set<string>} sectionsToUpdate - Sections to render.
   */
  async #recoverFromCartError(line, lineKey, parsedResponseText, sectionsToUpdate) {
    let cart = null;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (response.ok) cart = await response.json();
    } catch (error) {
      cart = null;
    }

    if (!cart?.items || !cart.sections?.[this.sectionId]) {
      // The real cart could not be read: fall back to the old reset + message.
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    await this.#updateEngravingFeeIfNeeded(cart, line, null, sectionsToUpdate);

    const sectionHTML = cart.sections[this.sectionId];
    const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: cart.sections,
      })
    );

    await morphSection(this.sectionId, sectionHTML);

    // Show the message on the re-rendered line (found by key; the line number as a fallback).
    const rows = Array.from(this.querySelectorAll('[ref="cartItemRows[]"]'));
    const row =
      (lineKey && rows.find((element) => element.getAttribute('data-key') === lineKey)) ||
      this.querySelector(`[data-cart-line="${line}"]`)?.closest('[ref="cartItemRows[]"]');
    const container = row?.querySelector('[ref^="cartItemErrorContainer-"]');
    const message = container?.querySelector('[ref^="cartItemError-"]');
    if (container && message) {
      message.textContent = parsedResponseText.errors;
      container.classList.remove('hidden');
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      try {
        Promise.resolve(morphSection(this.sectionId, cartItemsHtml)).catch((error) => {
          console.error('Cart section morph failed, refetching section', error);
          this.#refetchCartSection();
        });
      } catch (error) {
        console.error('Cart section morph failed, refetching section', error);
        this.#refetchCartSection();
      }
    } else {
      this.#refetchCartSection();
    }
  };

  /**
   * Refetches the cart items section (one retry), showing the existing
   * disabled/loading state while the drawer content is stale.
   *
   * @param {number} [retries] - Remaining retries after the first attempt.
   */
  #refetchCartSection = async (retries = 1) => {
    this.#disableCartItems();
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      if (retries > 0) {
        await this.#refetchCartSection(retries - 1);
        return;
      }
      console.error('Failed to refresh the cart section', error);
    } finally {
      this.#enableCartItems();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
